import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { surfaceAt, type Level } from "../level";
import { vehiclePose, type Vehicle } from "../vehicles";
import { disposeWorld } from "./level-meshes";
import { inked, toon } from "./toon";

// The map's vehicles (#37): a little beach buggy per loop, driven round it
// by the sim's clock (`update(level, clock)` each frame, so it's wherever
// the sim says). Built in the active art style, so it's rebuilt with the
// park. Local +z is the cart's nose, +x its left, y up from the floor.

const BODY = "#f4a261";
const TRIM = "#e9f5f9";
const DARK = "#2b2d42";
const CANOPY = "#2a9d8f";

function buildCart(v: Vehicle): THREE.Group {
  const cart = new THREE.Group();
  cart.name = "cart";
  const { hl, hw, h } = v;
  const body = toon(BODY, { roughness: 0.45 }, "paint");
  const trim = toon(TRIM, { roughness: 0.5 }, "plastic");
  const dark = toon(DARK, { roughness: 0.8 }, "plastic");
  const metal = toon("#9aa3ad", { roughness: 0.35, metalness: 0.5 }, "metal");
  const canopy = toon(CANOPY, { roughness: 0.6 }, "cloth");
  const wheelR = 0.26;
  const floorY = wheelR + 0.08;

  // The tub: a low body with a bonnet up front and a seat back.
  const tub = inked(new RoundedBoxGeometry(hw * 2, 0.42, hl * 2, 3, 0.1), body, 0.02);
  tub.position.y = floorY + 0.21;
  const bonnet = inked(new RoundedBoxGeometry(hw * 1.9, 0.3, hl * 0.7, 3, 0.1), body, 0.02);
  bonnet.position.set(0, floorY + 0.55, hl * 0.62);
  const seat = inked(new RoundedBoxGeometry(hw * 1.7, 0.55, 0.28, 2, 0.08), trim, 0.015);
  seat.position.set(0, floorY + 0.68, -hl * 0.35);
  cart.add(tub, bonnet, seat);

  // Four posts and a canopy roof at the kind's height.
  const postH = h - floorY - 0.42 - 0.08;
  const postGeo = new THREE.CylinderGeometry(0.035, 0.035, postH, 8).translate(0, postH / 2, 0);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const post = new THREE.Mesh(postGeo, metal);
      post.position.set(sx * (hw - 0.08), floorY + 0.42, sz * hl * 0.55 - hl * 0.05);
      post.castShadow = true;
      cart.add(post);
    }
  }
  const roof = inked(new RoundedBoxGeometry(hw * 2.1, 0.08, hl * 1.45, 2, 0.03), canopy, 0.015);
  roof.position.set(0, h - 0.04, -hl * 0.05);
  cart.add(roof);

  // Wheels, and the back bumper a skitcher holds.
  const wheelGeo = new THREE.CylinderGeometry(wheelR, wheelR, 0.18, 16).rotateZ(Math.PI / 2);
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const wheel = inked(wheelGeo, dark, 0.012);
      wheel.position.set(sx * (hw - 0.05), wheelR, sz * hl * 0.62);
      cart.add(wheel);
    }
  }
  const bumper = inked(new RoundedBoxGeometry(hw * 1.8, 0.1, 0.1, 2, 0.04), metal, 0.012);
  bumper.position.set(0, floorY + 0.3, -hl - 0.03);
  const headlights = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.4, 0.08, 0.04), toon("#fff3b0", { roughness: 0.2 }, "glass"));
  headlights.position.set(0, floorY + 0.35, hl + 0.01);
  headlights.castShadow = true;
  cart.add(bumper, headlights);
  return cart;
}

export class VehicleMeshes {
  readonly root = new THREE.Group();
  private carts: THREE.Group[];

  constructor(private vehicles: readonly Vehicle[]) {
    this.root.name = "vehicles";
    this.carts = vehicles.map(buildCart);
    this.root.add(...this.carts);
  }

  /** Puts every cart where the sim has it at `clock`. */
  update(level: Level, clock: number): void {
    for (const [i, v] of this.vehicles.entries()) {
      const p = vehiclePose(v, clock);
      const cart = this.carts[i]!;
      cart.position.set(p.x, surfaceAt(level, p.x, p.z).h, p.z);
      cart.rotation.y = p.yaw;
    }
  }

  dispose(): void {
    disposeWorld(this.root);
  }
}
